/**
 * Round 16 O5 -- what his group means, on the owner's own page.
 *
 * His IBS page carried "GROUP · Everyday payments" linking to the tab,
 * and nothing of what that means: that the board reads his service and
 * its neighbours as one line, that the line's tolerance is the
 * tightest member's, and whose that is -- which may not be his. An
 * ungrouped service said nothing about groups at all. A Critical
 * Internal Function is never in a group and says so elsewhere; this
 * says nothing for one.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "shared/api/client";
import { useIBSStory } from "twin/api/hooks";

interface GroupsState {
  groups: {
    code: string;
    name: string;
    derived_tolerance_hours: number | null;
    derived_tolerance_from: string | null;
    members: { ibs_service_id: string; name: string }[];
  }[];
}

export function IBSGroupNote({ ibsId }: { ibsId: string | undefined }) {
  const { t, i18n } = useTranslation();
  const story = useIBSStory(ibsId);
  const groups = useQuery({
    queryKey: ["twin", "ibs-groups"],
    queryFn: () => api.get<GroupsState>("/v1/twin/ibs-groups"),
    enabled: Boolean(ibsId),
  });
  const s = story.data;
  if (!s || !groups.data || s.service_class === "internal") return null;
  const list = Array.isArray(groups.data.groups) ? groups.data.groups : [];
  const mine = list.find((g) =>
    (g.members ?? []).some((m) => m.ibs_service_id === ibsId),
  );

  let sentence: string;
  if (mine) {
    const others = mine.members
      .filter((m) => m.ibs_service_id !== ibsId)
      .map((m) => m.name);
    const together =
      others.length === 0
        ? t("modules.twin.ibsGroupNote.alone")
        : t("modules.twin.ibsGroupNote.withOthers", {
            others: new Intl.ListFormat(i18n.language, {
              style: "long",
              type: "conjunction",
            }).format(others),
          });
    sentence = t(
      mine.derived_tolerance_hours === null
        ? "modules.twin.ibsGroupNote.inGroupNoTolerance"
        : "modules.twin.ibsGroupNote.inGroup",
      {
        group: mine.name,
        together,
        hours: mine.derived_tolerance_hours,
        from: mine.derived_tolerance_from,
      },
    );
  } else {
    sentence = t(
      s.admission?.status === "admitted"
        ? "modules.twin.ibsGroupNote.ungroupedAdmitted"
        : "modules.twin.ibsGroupNote.ungrouped",
    );
  }

  return (
    <p data-testid="ibs-group-note" className="text-sm text-slate-700">
      {sentence}{" "}
      {mine ? (
        <Link
          to={`/twin/ibs-groups#${mine.code}`}
          className="text-brand-brassDark hover:underline"
        >
          {t("modules.twin.ibsGroupNote.open")}
        </Link>
      ) : null}
    </p>
  );
}
